import type { TFunction } from 'i18next'
import { ApiError } from '@/services/api/client'
import type { ValidationFinding } from '@/services/api/schemas'
import type { SchemaEditKind } from './fork'

/**
 * Pull schema-validation findings out of a caught error.
 *
 * Returns the findings array only when `error` is an {@link ApiError} carrying
 * a 422 response with a `data.detail.findings` array (the locked validation
 * contract). For any other shape — non-`ApiError`, a different status, or a
 * missing/malformed body — returns `null` so callers fall back to generic
 * error handling. Defensive about every level of the shape.
 */
export function extractValidationFindings(error: unknown): ValidationFinding[] | null {
  if (!(error instanceof ApiError) || error.status !== 422) {
    return null
  }

  const data = error.data
  if (typeof data !== 'object' || data === null) {
    return null
  }

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail !== 'object' || detail === null) {
    return null
  }

  const findings = (detail as { findings?: unknown }).findings
  if (!Array.isArray(findings)) {
    return null
  }

  return findings as ValidationFinding[]
}

/**
 * One-line summary of blocking findings for a toast.
 *
 * Used where there is no open dialog to render the findings inline — e.g. a
 * delete rejected by the save gate (deleting `Person`) is triggered from a
 * detail card, so the reason is surfaced in the error toast instead.
 *
 * Bounded to `maxShown` messages with an "(and N more)" tail so a schema with
 * many blocking findings can't blow the toast up into a wall of text.
 */
export function summarizeFindings(
  findings: ValidationFinding[],
  t: TFunction,
  maxShown = 2
): string {
  if (findings.length === 0) return ''
  const shown = findings.slice(0, maxShown).map(f => f.message)
  const remaining = findings.length - shown.length
  const summary = shown.join('; ')
  return remaining > 0
    ? `${summary} ${t('schemaAgent.editor.toast.moreFindings', { count: remaining })}`
    : summary
}

/**
 * Toast message for a delete the save gate rejected.
 *
 * If the error carries validation findings, name the blocking rule(s); otherwise
 * fall back to a generic failure. `kind` is the deleted thing.
 */
export function deleteErrorMessage(error: unknown, kind: SchemaEditKind, t: TFunction): string {
  const findings = extractValidationFindings(error)
  return findings
    ? t(`schemaAgent.editor.toast.${kind}.deleteBlocked`, {
        reason: summarizeFindings(findings, t),
      })
    : t(`schemaAgent.editor.toast.${kind}.deleteFailed`)
}
